// Sections 9 and 10 (stream M3a): read-only numbers for the Build tab and the Tools panel, so the
// view models show exactly what the phases will do: an order's cost when it starts (repairs by
// damage, B-05), upkeep, next season's build points, tool stock and demand, and housing.
import { config, type Config } from '../config';
import { isWorking, peopleSorted } from '../core/queries';
import type {
  BuildingId,
  BuildingTypeId,
  Decision,
  GameState,
  HouseholdId,
  JobId,
  Person,
  Resource,
} from '../core/types';
import { repairMaterials, repairShare, upkeepCost } from '../phases/shared/capacity';
import { workBlock } from '../phases/shared/eligibility';
import { totalBeds } from '../phases/shared/housing';
import {
  effectiveTools,
  seasonsWithoutToolmaker,
  toolsOnHand,
  wantsTool,
} from '../phases/shared/tools';
import { valueOf } from '../phases/shared/value';
import { readContext } from './context';

export interface OrderCost {
  /** Build points (the `buildCost` target, scaled by damage for a repair). */
  points: number;
  /** Materials deducted when construction starts. */
  materials: Partial<Record<Resource, number>>;
}

/** Section 9: what an order costs when construction starts (repairs by the damage then). */
export function orderCost(
  state: GameState,
  buildingType: BuildingTypeId,
  repairOf: BuildingId | null,
  cfg: Config = config,
): OrderCost {
  const ctx = readContext(state, cfg);
  const points = valueOf(ctx, { kind: 'buildCost', buildingType }).value;
  if (repairOf === null)
    return { points, materials: ctx.registry.buildings[buildingType].materials };
  const b = state.buildings[repairOf];
  if (b === undefined) return { points: 0, materials: {} };
  return { points: points * repairShare(b), materials: repairMaterials(ctx.registry, b) };
}

/** Section 9: upkeep due every 8 seasons (10% of the materials). */
export function upkeepOf(
  state: GameState,
  buildingId: BuildingId,
  cfg: Config = config,
): Partial<Record<Resource, number>> {
  const b = state.buildings[buildingId];
  if (b === undefined) return {};
  return upkeepCost(readContext(state, cfg).registry, cfg, b.type);
}

/** People as they will work next season: the state plus the draft's assignments. */
function withDraftJobs(state: GameState, decision: Decision | null): Person[] {
  return peopleSorted(state).map((p) => {
    const job: JobId | undefined = decision?.assignments[p.id];
    return job === undefined || job === p.job ? p : { ...p, job };
  });
}

/**
 * Section 9: build points builders will pour in next season under the draft's assignments,
 * through the pipeline (each builder's output plus building services), for "seasons to
 * completion at the current builder count".
 */
export function buildPointsForecast(
  state: GameState,
  decision: Decision | null,
  cfg: Config = config,
): { builders: number; points: number } {
  const ctx = readContext(state, cfg);
  let builders = 0;
  let points = valueOf(ctx, { kind: 'service', service: 'buildPoints' }).value;
  for (const p of withDraftJobs(state, decision)) {
    if (p.job !== 'builder' || !isWorking(p) || workBlock(ctx, p, p.job) !== null) continue;
    builders++;
    points += Math.max(0, valueOf(ctx, { kind: 'output', personId: p.id, jobId: 'builder' }).value);
  }
  return { builders, points };
}

export interface ToolStock {
  /** Fractional tool units in stock. */
  amount: number;
  /** Whole tools that can be handed out this season. */
  onHand: number;
  /** Mean durability 0..1. */
  durability: number;
  /** amount x durability (Section 10: 20 tools at 0.5 = 10 effective tools). */
  effective: number;
  /** Workers who want a tool under the draft's assignments. */
  demand: number;
  /** demand - onHand, at least 0. */
  shortfall: number;
  seasonsWithoutToolmaker: number;
  /** Who wants a tool next season. */
  wanting: string[];
}

/** Section 10 Tools panel numbers under the draft's assignments. */
export function toolStock(
  state: GameState,
  decision: Decision | null,
  cfg: Config = config,
): ToolStock {
  const registry = readContext(state, cfg).registry;
  const wanting = withDraftJobs(state, decision)
    .filter((p) => wantsTool(registry, p))
    .map((p) => p.id);
  const onHand = toolsOnHand(state);
  return {
    amount: state.stocks.tools.amount,
    onHand,
    durability: state.toolDurability,
    effective: effectiveTools(state),
    demand: wanting.length,
    shortfall: Math.max(0, wanting.length - onHand),
    seasonsWithoutToolmaker: seasonsWithoutToolmaker(state, state.turn),
    wanting,
  };
}

export interface HousingSummary {
  beds: number;
  people: number;
  /** People whose household has no building (sleeping rough last season). */
  rough: number;
  /** Households with members over bed capacity last season. */
  crowdedHouseholds: HouseholdId[];
}

/** Section 9: beds against people, as housed at the start of the last season. */
export function housingSummary(state: GameState, cfg: Config = config): HousingSummary {
  const registry = readContext(state, cfg).registry;
  let rough = 0;
  for (const h of Object.values(state.households))
    if (h.buildingId === null) rough += h.memberIds.length;
  const report = state.history[state.history.length - 1];
  return {
    beds: totalBeds(registry, state),
    people: Object.keys(state.people).length,
    rough,
    crowdedHouseholds: Object.keys(report?.crowding ?? {}).sort() as HouseholdId[],
  };
}
